import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Container,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  InputLabel,
  FormGroup,
  Autocomplete,
  CircularProgress,
  Grid,
  Chip,
  FormControl,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Cookies from "js-cookie";
import SetSkills from "./SetSkills";
import ReviewCreateJob from "./ReviewCreateJob";
import CompanyCultureFit from "./CompanyCultureFit";
import DefineInterviewProcess from "./DefineInterviewProcess";
import { employmentTypes, experienceLevels, skillLevels } from "./Constants";
import axiosInstance from "../../axios/axiosInstance";

const fluencyLevels = [
  "Basic",
  "Intermediate",
  "Business fluent",
  "Bilingual/Native",
];
const languages = [
  { name: "English" },
  { name: "French" },
  { name: "German" },
  { name: "Italian" },
  { name: "Spanish" },
  { name: "Dutch" },
  { name: "Portuguese" },
  { name: "Polish" },
  { name: "Czech" },
  { name: "Danish" },
  { name: "Swedish" },
  { name: "Finnish" },
  { name: "Norwegian" },
  { name: "Icelandic" },
  { name: "Romanian" },
  { name: "Hungarian" },
  { name: "Greek" },
  { name: "Slovak" },
  { name: "Slovenian" },
  { name: "Croatian" },
  { name: "Bulgarian" },
  { name: "Estonian" },
  { name: "Latvian" },
  { name: "Lithuanian" },
  { name: "Maltese" },
  { name: "Irish" },
  { name: "Romansh" },
];

const CreateJob = () => {
  const navigate = useNavigate();
  const { jobId } = useParams();
  const isEditMode = !!jobId;
  const [activeStep, setActiveStep] = useState(0);
  const [loadingJobData, setLoadingJobData] = useState(isEditMode);
  const [form, setForm] = useState({
    functions: "",
    confidential: false,
    position: "",
    countries: "",
    summary: "",
    employmentType: [],
    experienceLevel: "",
    whyRoleOpen: "",
    whyJoinTeam: "",
    typeOfCompany: [],
    workEnvironment: [],
    languages: [],
  });

  const [interviewStages, setInterviewStages] = useState([]);

  const [skillsData, setSkillsData] = useState(null);
  const [functions, setFunctions] = useState([]);
  const [roles, setRoles] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [loadingFunctions, setLoadingFunctions] = useState(true);
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [softSkillsNames, setSoftSkillsNames] = useState([]);
  const token = Cookies.get("token");
  const [localCountries, setLocalCountries] = useState([]);
  const [countrySearch, setCountrySearch] = useState("");
  const [languagesList, setLanguagesList] = useState([]);

  useEffect(() => {
    const fetchFunctions = async () => {
      setLoadingFunctions(true);
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/api/profile/job-functions`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (res.ok) {
          const data = await res.json();
          setFunctions(data);
        }
      } finally {
        setLoadingFunctions(false);
      }
    };
    fetchFunctions();
  }, [token]);

  useEffect(() => {
    const fetchCountries = async () => {
      setLoadingCountries(true);
      console.log(localCountries);
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/api/profile/countries`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        if (res.ok) {
          const data = await res.json();
          console.log(data);
          setLocalCountries(data);
          setCountries(data); // maintain your existing countries list
          if (!isEditMode && data.length > 0) {
            setForm((prev) => ({ ...prev, countries: 1 }));
          }
        }
      } catch (error) {
        console.error("Failed to load countries:", error);
      } finally {
        setLoadingCountries(false);
      }
    };
    fetchCountries();
  }, [token]);

  const fetchCountriesSearch = async (query) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL
        }/api/profile/countries?search=${encodeURIComponent(query)}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (!res.ok) throw new Error("Failed to search countries");
      const data = await res.json();
      setLocalCountries(data);
    } catch (error) {
      console.error("Error fetching country search results:", error);
    }
  };

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (countrySearch.trim().length > 0) {
        fetchCountriesSearch(countrySearch);
      } else {
        setLocalCountries(countries);
      }
    }, 400);

    return () => clearTimeout(delayDebounce);
  }, [countrySearch, countries]);

  useEffect(() => {
    if (!form.functions) {
      setRoles([]);
      return;
    }
    const fetchRoles = async () => {
      setLoadingRoles(true);
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/api/profile/job-roles/${form.functions}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (res.ok) {
          const data = await res.json();
          setRoles(data);
        } else {
          setRoles([]);
        }
      } finally {
        setLoadingRoles(false);
      }
    };
    fetchRoles();
  }, [form.functions, token]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newValue;
    if (name === "countries") {
      newValue = value === "" ? "" : Number(value);
    } else {
      newValue = type === "checkbox" ? checked : value;
    }
    setForm((prev) => ({
      ...prev,
      [name]: newValue,
      ...(name === "functions" ? { position: "" } : {}),
    }));
  };

  const handleEmploymentTypeChange = (type) => {
    setForm((prev) => {
      const current = prev.employmentType;
      const updated = current.includes(type)
        ? current.filter((t) => t !== type)
        : [...current, type];
      return { ...prev, employmentType: updated };
    });
  };

  const isFormValid =
    form.functions &&
    form.position &&
    form.countries &&
    form.summary &&
    form.experienceLevel &&
    form.employmentType.length > 0;
  // form.whyJoinTeam &&
  // form.whyRoleOpen;

  const handleNext = () => setActiveStep((prev) => prev + 1);
  const handleBack = () => setActiveStep((prev) => prev - 1);

  const [selectedSoftSkills, setSelectedSoftSkills] = useState([]);

  // Fetch job data for editing
  useEffect(() => {
    const fetchJobData = async () => {
      if (!isEditMode || !jobId) return;

      try {
        setLoadingJobData(true);
        const response = await axiosInstance.get(
          `api/job-postings/job/${jobId}`,
        );
        const jobData = response.data;

        // Skills are in the response
        const skills =
          jobData.skills && jobData.skills.length > 0
            ? jobData.skills[0]
            : null;

        const interviewStages =
          jobData.interviewStages || jobData.interview_stages || [];

        setInterviewStages(interviewStages);
        // Transform skills data to match SetSkills format
        const transformedSkills = {};
        if (skills) {
          if (skills.selected_must_have_skills) {
            const parsed = Array.isArray(skills.selected_must_have_skills)
              ? skills.selected_must_have_skills
              : typeof skills.selected_must_have_skills === "string"
                ? JSON.parse(skills.selected_must_have_skills)
                : [];
            parsed.forEach((skillObj) => {
              const skillName = Object.keys(skillObj)[0];
              const skillData = skillObj[skillName];
              // Handle level: if number (1-4), use directly; if string, convert to number
              let levelNumber = 1;
              if (typeof skillData.level === "number") {
                // Backend returns number (1-4), use directly but ensure it's in valid range
                levelNumber = Math.max(1, Math.min(4, skillData.level));
              } else if (typeof skillData.level === "string") {
                // Backend returns string (e.g., "Beginner"), convert to number (1-4)
                const levelIndex = skillLevels.indexOf(skillData.level);
                levelNumber = levelIndex !== -1 ? levelIndex + 1 : 1;
              }
              transformedSkills[`must|${skillName}`] = {
                level: levelNumber,
                subSkills: (
                  skillData.subSkills ||
                  skillData.subSkill ||
                  []
                ).map((sub) => {
                  if (typeof sub === "string")
                    return { name: sub, level: levelNumber };
                  let subLevelNumber = 1;
                  if (typeof sub.level === "number") {
                    subLevelNumber = Math.max(1, Math.min(4, sub.level));
                  } else if (typeof sub.level === "string") {
                    const levelIndex = skillLevels.indexOf(sub.level);
                    subLevelNumber = levelIndex !== -1 ? levelIndex + 1 : 1;
                  }
                  return { name: sub.name || sub, level: subLevelNumber };
                }),
              };
            });
          }
          if (skills.selected_should_have_skills) {
            const parsed = Array.isArray(skills.selected_should_have_skills)
              ? skills.selected_should_have_skills
              : typeof skills.selected_should_have_skills === "string"
                ? JSON.parse(skills.selected_should_have_skills)
                : [];
            parsed.forEach((skillObj) => {
              const skillName = Object.keys(skillObj)[0];
              const skillData = skillObj[skillName];
              // Handle level: if number (1-4), use directly; if string, convert to number
              let levelNumber = 1;
              if (typeof skillData.level === "number") {
                // Backend returns number (1-4), use directly but ensure it's in valid range
                levelNumber = Math.max(1, Math.min(4, skillData.level));
              } else if (typeof skillData.level === "string") {
                // Backend returns string (e.g., "Beginner"), convert to number (1-4)
                const levelIndex = skillLevels.indexOf(skillData.level);
                levelNumber = levelIndex !== -1 ? levelIndex + 1 : 1;
              }
              transformedSkills[`should|${skillName}`] = {
                level: levelNumber,
                subSkills: (
                  skillData.subSkills ||
                  skillData.subSkill ||
                  []
                ).map((sub) => {
                  if (typeof sub === "string")
                    return { name: sub, level: levelNumber };
                  let subLevelNumber = 1;
                  if (typeof sub.level === "number") {
                    subLevelNumber = Math.max(1, Math.min(4, sub.level));
                  } else if (typeof sub.level === "string") {
                    const levelIndex = skillLevels.indexOf(sub.level);
                    subLevelNumber = levelIndex !== -1 ? levelIndex + 1 : 1;
                  }
                  return { name: sub.name || sub, level: subLevelNumber };
                }),
              };
            });
          }
          if (skills.selected_could_have_skills) {
            const parsed = Array.isArray(skills.selected_could_have_skills)
              ? skills.selected_could_have_skills
              : typeof skills.selected_could_have_skills === "string"
                ? JSON.parse(skills.selected_could_have_skills)
                : [];
            parsed.forEach((skillObj) => {
              const skillName = Object.keys(skillObj)[0];
              const skillData = skillObj[skillName];
              // Handle level: if number (1-4), use directly; if string, convert to number
              let levelNumber = 1;
              if (typeof skillData.level === "number") {
                // Backend returns number (1-4), use directly but ensure it's in valid range
                levelNumber = Math.max(1, Math.min(4, skillData.level));
              } else if (typeof skillData.level === "string") {
                // Backend returns string (e.g., "Beginner"), convert to number (1-4)
                const levelIndex = skillLevels.indexOf(skillData.level);
                levelNumber = levelIndex !== -1 ? levelIndex + 1 : 1;
              }
              transformedSkills[`could|${skillName}`] = {
                level: levelNumber,
                subSkills: (
                  skillData.subSkills ||
                  skillData.subSkill ||
                  []
                ).map((sub) => {
                  if (typeof sub === "string")
                    return { name: sub, level: levelNumber };
                  let subLevelNumber = 1;
                  if (typeof sub.level === "number") {
                    subLevelNumber = Math.max(1, Math.min(4, sub.level));
                  } else if (typeof sub.level === "string") {
                    const levelIndex = skillLevels.indexOf(sub.level);
                    subLevelNumber = levelIndex !== -1 ? levelIndex + 1 : 1;
                  }
                  return { name: sub.name || sub, level: subLevelNumber };
                }),
              };
            });
          }
        }

        setSkillsData(transformedSkills);

        // Transform languages - languages will be handled in SetSkills component
        const languagesData = jobData.language_requirements || [];
        const languagesListData = languagesData.map((lang) => ({
          name: lang.language,
          fluency: lang.fluency,
        }));
        setLanguagesList(languagesListData);

        // Populate form
        setForm({
          functions: jobData.job_function_id || "",
          confidential: jobData.is_confidential || false,
          position: jobData.job_role_id || "",
          countries: jobData.country_id || "",
          summary: jobData.summary || "",
          employmentType: Array.isArray(jobData.employment_type)
            ? jobData.employment_type
            : jobData.employment_type
              ? [jobData.employment_type]
              : [],
          experienceLevel: jobData.experience_level || "",
          whyRoleOpen: jobData.role_open_reason || "",
          whyJoinTeam: jobData.why_join_team || "",
          typeOfCompany: Array.isArray(jobData.company_type)
            ? jobData.company_type
            : jobData.company_type
              ? [jobData.company_type]
              : [],
          workEnvironment: Array.isArray(jobData.work_environment)
            ? jobData.work_environment
            : jobData.work_environment
              ? [jobData.work_environment]
              : [],
          languages: languagesListData,
        });

        // Set soft skills
        if (jobData.selected_soft_skills) {
          const softSkills = Array.isArray(jobData.selected_soft_skills)
            ? jobData.selected_soft_skills
            : typeof jobData.selected_soft_skills === "string"
              ? JSON.parse(jobData.selected_soft_skills)
              : [];
          setSelectedSoftSkills(softSkills);
        }
      } catch (error) {
        console.error("Error fetching job data:", error);
      } finally {
        setLoadingJobData(false);
      }
    };

    fetchJobData();
  }, [jobId, isEditMode]);

  // Update the SetSkills render to pass proper props
  if (loadingJobData) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (activeStep === 1) {
    return (
      <SetSkills
        formData={form}
        skillsData={skillsData} // Pass existing skills data
        setSkillsData={setSkillsData} // Pass setter to update parent state
        selectedSoftSkills={selectedSoftSkills}
        setSelectedSoftSkills={setSelectedSoftSkills}
        setSoftSkillsNames={setSoftSkillsNames}
        onBack={handleBack}
        onNext={handleNext}
        languages={languagesList}
        setLanguages={setLanguagesList}
        formLanguages={form.languages}
        setFormLanguages={(langs) =>
          setForm((prev) => ({ ...prev, languages: langs }))
        }
        languagesOptions={languages}
        fluencyLevels={fluencyLevels}
      />
    );
  }

  // Step 3: Company Culture Fit
  if (activeStep === 2) {
    return (
      <CompanyCultureFit
        form={form}
        setForm={setForm}
        onNext={handleNext}
        onBack={handleBack}
      />
    );
  }

  // Step 4: Define Interview Process
  if (activeStep === 3) {
    return (
      <DefineInterviewProcess
        interviewStages={interviewStages}
        setInterviewStages={setInterviewStages}
        onNext={handleNext}
        onBack={handleBack}
      />
    );
  }

  // Step 5: Review
  if (activeStep === 4) {
    return (
      <ReviewCreateJob
        formData={form}
        skillsData={skillsData}
        selectedSoftSkills={selectedSoftSkills}
        functions={functions}
        roles={roles}
        countries={countries}
        softSkillNames={softSkillsNames}
        onBack={handleBack}
        onPublish={() => navigate("/corporate/job-postings")}
        interviewStages={interviewStages}
        jobId={jobId}
        isEditMode={isEditMode}
      />
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 6 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate("/corporate/job-postings")}
        sx={{ mb: 2 }}
      >
        Back to Job Postings
      </Button>

      <Paper sx={{ p: 4, borderRadius: 3 }}>
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
          <Step>
            <StepLabel>Create a role</StepLabel>
          </Step>
          <Step>
            <StepLabel>Set skills</StepLabel>
          </Step>
          <Step>
            <StepLabel>Company type & culture Fit</StepLabel>
          </Step>
          <Step>
            <StepLabel>Define interview process</StepLabel>
          </Step>
          <Step>
            <StepLabel>Review & Publish</StepLabel>
          </Step>
        </Stepper>

        <Typography variant="h5" sx={{ mb: 3 }}>
          {isEditMode ? "Edit Job Posting" : "Create new job"}
        </Typography>
        <Typography variant="subtitle1" sx={{ mb: 3 }}>
          {isEditMode
            ? "Update the job posting details and republish."
            : "Create a new job position and define the process of the interviews."}
        </Typography>

        <Box component="form" autoComplete="off">
          <InputLabel id="experience-level-label">
            Select Experience Level *
          </InputLabel>
          <Select
            labelId="experience-level-label"
            name="experienceLevel"
            value={form.experienceLevel}
            onChange={handleChange}
            fullWidth
            sx={{ mb: 3 }}
            displayEmpty
          >
            <MenuItem value="">
              <em>Choose experience level</em>
            </MenuItem>
            {experienceLevels.map((level) => (
              <MenuItem key={level.value} value={level.value}>
                {level.label}
              </MenuItem>
            ))}
          </Select>

          <InputLabel id="functions-label">Functions *</InputLabel>
          <Select
            labelId="functions-label"
            name="functions"
            value={form.functions}
            onChange={handleChange}
            fullWidth
            sx={{ mb: 2 }}
            displayEmpty
            disabled={loadingFunctions}
          >
            <MenuItem value="">
              <em>{loadingFunctions ? "Loading..." : "Select functions"}</em>
            </MenuItem>
            {functions.map((func) => (
              <MenuItem key={func.id} value={func.id}>
                {func.name}
              </MenuItem>
            ))}
          </Select>

          <FormControlLabel
            control={
              <Checkbox
                checked={form.confidential}
                onChange={handleChange}
                name="confidential"
              />
            }
            label="Is this job confidential?"
            sx={{ mb: 2 }}
          />

          <InputLabel id="position-label">Position *</InputLabel>
          <Select
            labelId="position-label"
            name="position"
            value={form.position}
            onChange={handleChange}
            fullWidth
            sx={{ mb: 2 }}
            displayEmpty
            disabled={!form.functions || loadingRoles}
          >
            <MenuItem value="">
              <em>{loadingRoles ? "Loading..." : "Select relevant role"}</em>
            </MenuItem>
            {roles.map((role) => (
              <MenuItem key={role.id} value={role.id}>
                {role.name}
              </MenuItem>
            ))}
          </Select>

          {/* <InputLabel id="country-label">Country *</InputLabel>
          <Select
            labelId="country-label"
            name="countries"
            value={form.countries}
            onChange={handleChange}
            fullWidth
            sx={{ mb: 2 }}
            disabled={loadingCountries}
            displayEmpty
          >
            <MenuItem value="">
              <em>{loadingCountries ? "Loading..." : "Select country"}</em>
            </MenuItem>
            {countries.map((country) => (
              <MenuItem key={country.id} value={country.id}>
                {country.name}
              </MenuItem>
            ))}
          </Select> */}

          <InputLabel id="country-label">Country *</InputLabel>
          <Autocomplete
            disabled
            options={localCountries}
            getOptionLabel={(option) => option.name || ""}
            value={localCountries.find((c) => c.id === form.countries) || null}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(event, newValue) => {
              setForm((prev) => ({
                ...prev,
                countries: newValue ? newValue.id : "",
              }));
            }}
            onInputChange={(event, value) => {
              setCountrySearch(value);
            }}
            loading={loadingCountries}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search and select country"
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingCountries ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            sx={{ mb: 2 }}
          />

          <TextField
            label="Job summary *"
            name="summary"
            value={form.summary}
            onChange={handleChange}
            fullWidth
            multiline
            minRows={6}
            sx={{ mb: 2 }}
          />

          {/* <TextField
            label="Why join your team *"
            name="whyJoinTeam"
            value={form.whyJoinTeam}
            onChange={handleChange}
            fullWidth
            multiline
            minRows={6}
            sx={{ mb: 2 }}
            helperText={`${275 - form.whyJoinTeam.length} characters left`}
            inputProps={{ maxLength: 275 }}
          /> */}

          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Employment Type *
          </Typography>
          <FormGroup row sx={{ mb: 2 }}>
            {employmentTypes.map((type) => (
              <FormControlLabel
                key={type}
                control={
                  <Checkbox
                    checked={form.employmentType.includes(type)}
                    onChange={() => handleEmploymentTypeChange(type)}
                  />
                }
                label={type}
              />
            ))}
          </FormGroup>

          {/* <InputLabel id="why-role-open-label">
            Why is the role open? *
          </InputLabel>
          <Select
            labelId="why-role-open-label"
            name="whyRoleOpen"
            value={form.whyRoleOpen}
            onChange={handleChange}
            fullWidth
            sx={{ mb: 3 }}
            displayEmpty
          >
            <MenuItem value="">
              <em>Choose reason</em>
            </MenuItem>
            {roleOpenOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select> */}

          <Button
            variant="contained"
            color="primary"
            sx={{ mt: 2 }}
            disabled={!isFormValid}
            onClick={handleNext}
          >
            Next
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default CreateJob;
